import { useEffect, useRef, type ReactNode } from "react";
import { Button, cx } from "./ui";

/**
 * Modal built on <dialog> so focus trapping, Escape, and the top layer come
 * from the platform rather than a hand-rolled implementation.
 *
 * On phones it becomes a bottom sheet — full width, rounded top corners,
 * capped height — which is far easier to reach one-handed than a centred box.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = "md",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Escape fires <dialog>'s cancel event; route it through onClose so the
  // parent's state stays the single source of truth for open/closed.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const cancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener("cancel", cancel);
    return () => dialog.removeEventListener("cancel", cancel);
  }, [onClose]);

  const widths = { sm: "sm:max-w-md", md: "sm:max-w-2xl", lg: "sm:max-w-5xl" } as const;

  return (
    <dialog
      ref={ref}
      className={cx(
        "backdrop:bg-black/60 open:flex m-0 max-h-full w-full max-w-full flex-col bg-transparent p-0",
        "fixed inset-0 h-full items-end justify-center sm:items-center",
      )}
      onClick={(event) => {
        // Clicking the backdrop closes; clicks inside the panel must not.
        if (event.target === ref.current) onClose();
      }}
    >
      <div
        className={cx(
          "flex max-h-[92dvh] w-full flex-col overflow-hidden border border-line bg-surface",
          "rounded-t-2xl sm:max-h-[88dvh] sm:rounded-xl",
          widths[size],
        )}
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2 className="text-base font-semibold text-fg">{title}</h2>
          <Button variant="ghost" onClick={onClose} aria-label="Close" className="px-2 py-1">
            <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </Button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>

        {footer ? (
          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line px-4 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </dialog>
  );
}

/**
 * Confirmation prompt. `danger` marks the destructive variant — the desktop
 * app always confirms before clearing a status tick or deleting a record, and
 * the web app must not be the looser of the two.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} disabled={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-fg-secondary">{message}</div>
    </Modal>
  );
}
